import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import axiosInstance from '@libs/axios';
import { apiUrl } from '@constants/url';
import type {
  AcknowledgePatchLogsPayload,
  AcknowledgePatchLogsResponse,
  DeferPatchLogsPayload,
  DeferPatchLogsResponse,
  PatchLogPublic,
  UnreadPatchLogsResponse,
} from '@/features/patch-logs/types';

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: { code?: string; message?: string };
}

export const patchLogQueryKeys = {
  all: ['patch-logs'] as const,
  unread: ['patch-logs', 'unread'] as const,
  published: ['patch-logs', 'published'] as const,
};

const EMPTY_UNREAD: UnreadPatchLogsResponse = { logs: [], badgeOnlyCount: 0, unreadCount: 0 };

/**
 * Unread published logs for the signed-in user (server filters by account
 * age, acknowledgments and the 3-deferral cap). Fails SILENTLY: a network or
 * server error resolves to "nothing unread" so the dashboard is never blocked
 * by the feed itself. Callers must be authenticated — the query runs as soon
 * as it is enabled.
 */
export function useUnreadPatchLogs(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: patchLogQueryKeys.unread,
    queryFn: async ({ signal }): Promise<UnreadPatchLogsResponse> => {
      try {
        const response = await axiosInstance.get<ApiEnvelope<UnreadPatchLogsResponse>>(apiUrl.patchLogs.unread, {
          signal,
        });
        const payload = response.data?.data;
        const logs = Array.isArray(payload?.logs) ? payload.logs : [];
        const badgeOnlyCount = typeof payload?.badgeOnlyCount === 'number' ? payload.badgeOnlyCount : 0;
        return {
          logs,
          badgeOnlyCount,
          // Older servers do not send it; the two lists we do have add up to
          // the same number, so the badge still works against them.
          unreadCount: typeof payload?.unreadCount === 'number' ? payload.unreadCount : logs.length + badgeOnlyCount,
        };
      } catch (error) {
        if (axios.isCancel(error)) {
          throw error;
        }
        console.warn('[patch-logs] unread fetch failed; showing nothing', error);
        return EMPTY_UNREAD;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
    enabled: options?.enabled ?? true,
  });
}

/** Published history (Issue 7). Exposed now so the history page has a hook to bind to. */
export function usePublishedPatchLogs(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: patchLogQueryKeys.published,
    queryFn: async ({ signal }) => {
      const response = await axiosInstance.get<ApiEnvelope<PatchLogPublic[]>>(apiUrl.patchLogs.published, { signal });
      return response.data.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
    enabled: options?.enabled ?? true,
  });
}

/** Idempotent on the server (unique per log + user), so a double click is harmless. */
export function useAcknowledgePatchLogs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AcknowledgePatchLogsPayload) => {
      const response = await axiosInstance.post<ApiEnvelope<AcknowledgePatchLogsResponse>>(
        apiUrl.patchLogs.acknowledge,
        payload
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: patchLogQueryKeys.all });
    },
  });
}

/** Each call bumps the per-log deferral count (server caps at 3). */
export function useDeferPatchLogs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: DeferPatchLogsPayload) => {
      const response = await axiosInstance.post<ApiEnvelope<DeferPatchLogsResponse>>(apiUrl.patchLogs.defer, payload);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: patchLogQueryKeys.all });
    },
  });
}

/**
 * The number for the "unread updates" badge (Issue 13). Reads the same query
 * as the gate, so the badge and the modal can never disagree, and stays > 0
 * after the modal has been deferred away — that persistence is the point.
 */
export function useUnreadPatchLogCount(options?: { enabled?: boolean }) {
  const { data } = useUnreadPatchLogs(options);
  return data?.unreadCount ?? 0;
}
